import React, { useState } from 'react';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import OrderListTemplate, { OrderListActionContext } from './components/OrderListTemplate';
import { MergedOrder, OrderTabScene } from '../../types/process/order';
import OrderTriggerActionMap, { OrderTriggerActionProvider, type OrderTriggerTarget } from './components/OrderTriggerActionMap';
import { useDefaultQueryFields } from './order.effect';

const { Text } = Typography;

const addWorkingHours = (start: Date, durationHours: number): Date => {
  const startHour = 8;
  const endHour = 20;
  let remainingMs = durationHours * 60 * 60 * 1000;
  let current = new Date(start.getTime());

  while (remainingMs > 0) {
    const y = current.getFullYear();
    const m = current.getMonth();
    const d = current.getDate();

    const dayStart = new Date(y, m, d, startHour, 0, 0, 0);
    const dayEnd = new Date(y, m, d, endHour, 0, 0, 0);

    if (current < dayStart) current = dayStart;
    if (current > dayEnd) {
      current = new Date(y, m, d + 1, startHour, 0, 0, 0);
      continue;
    }

    const availableMs = dayEnd.getTime() - current.getTime();
    if (remainingMs <= availableMs) {
      return new Date(current.getTime() + remainingMs);
    }

    remainingMs -= availableMs;
    current = new Date(y, m, d + 1, startHour, 0, 0, 0);
  }

  return current;
};

const OrderPreList: React.FC = () => {
  const queryFields = useDefaultQueryFields(OrderTabScene.GroupPre);

  const [refreshKey, setRefreshKey] = useState(0);
  const reloadList = () => setRefreshKey(k => k + 1);

  const renderListInfo = (order: MergedOrder, ctx?: OrderListActionContext) => {
    const sceneKey = ctx?.scene || '';
    const createdAt = order.order.orderCreatedAt || order.orderData?.orderCreatedAt;
    const createdAtDate = createdAt ? dayjs(createdAt).toDate() : undefined;
    const isCreatedAtValid = createdAtDate ? Number.isFinite(createdAtDate.getTime()) : false;

    let deadlineLabel: string | undefined;
    let deadlineValue: string | undefined;
    if (isCreatedAtValid && sceneKey === 'tab_pre_wait_response') {
      deadlineLabel = '响应截止时间';
      deadlineValue = dayjs(addWorkingHours(createdAtDate!, 2)).format('YYYY-MM-DD HH:mm:ss');
    } else if (isCreatedAtValid && sceneKey === 'tab_pre_wait_lock') {
      deadlineLabel = '锁单截止时间';
      deadlineValue = dayjs(addWorkingHours(createdAtDate!, 4)).format('YYYY-MM-DD HH:mm:ss');
    }

    if (!deadlineLabel) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Text style={{ color: '#ff4d4f' }}>
          {deadlineLabel}：{deadlineValue || '-'}
        </Text>
      </div>
    );
  };

  const renderListActions = (order: MergedOrder, ctx?: OrderListActionContext) => {
    const sceneKey = ctx?.scene || '';

    const targets: OrderTriggerTarget[] = [];
    if (sceneKey === 'tab_pre_all') {
      targets.push('reportToBuer', 'reportView', 'statusToAudit');
    } else if (sceneKey === 'tab_pre_wait_send') {
      targets.push('reportView', 'statusToAudit', 'cancelByShipIntercept', 'cancelByLogisticsIntercept');
    } else if (sceneKey === 'tab_pre_wait_cancel' || sceneKey === 'tab_pre_wait_intercept') {
      targets.push('reportView', 'cancelByOrder', 'cancelByShipIntercept', 'cancelByLogisticsIntercept');
    } else {
      // targets.push('reportView', 'cancelByOrder');
    }

    return (
      <OrderTriggerActionMap targets={targets} order={order} ctx={ctx} onReload={reloadList} />
    );
  };

  return (
    <OrderTriggerActionProvider onReload={reloadList}>
      <OrderListTemplate
        refreshKey={refreshKey}
        kpiScene="kpi:group_pre"
        tabScene={OrderTabScene.GroupPre}
        defaultScene='tab_pre_all'
        queryFields={queryFields}
        viewModes={[{ mode: 'list', label: '订单页', renderActions: renderListActions, renderInfo: renderListInfo }]} // Only list view for processing
      />
    </OrderTriggerActionProvider>
  );
};

export default OrderPreList;
